import { ChevronRight } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import type { SectionGroupProps, SectionProps } from './configTypes';

export function Section({
  title,
  sectionKey,
  children,
  isExpanded,
  onToggleExpand,
  enabledCount,
  totalCount,
  showCount = true,
  showToggleAll = false,
  comingSoon = false,
  onToggleAll,
  minEnabled,
}: SectionProps): ReactElement {
  const allEnabled = enabledCount === totalCount && totalCount > 0;
  const atMinimum = enabledCount <= (minEnabled ?? 0);

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => onToggleExpand(sectionKey)}
        className={`w-full flex items-center justify-between py-2.5 px-3 rounded-lg transition group ${
          isExpanded
            ? 'bg-theme-surface border border-theme-border'
            : 'bg-theme-surface/50 hover:bg-theme-surface border border-transparent'
        }`}
      >
        <div className="flex items-center gap-2">
          <ChevronRight
            className={`w-3.5 h-3.5 text-theme-text-tertiary transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
          />
          <h4
            className={`text-xs font-semibold uppercase tracking-wider transition ${
              isExpanded
                ? 'text-white'
                : 'text-theme-text-secondary group-hover:text-theme-text-secondary'
            }`}
          >
            {title}
          </h4>
          {comingSoon && <span className="text-xs text-theme-text-tertiary">(Coming soon)</span>}
        </div>
        {showCount && totalCount > 0 && (
          <span
            className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
              allEnabled
                ? 'bg-theme-success/30 text-theme-success'
                : enabledCount > 0
                  ? 'bg-theme-primary/30 text-theme-primary'
                  : 'bg-theme-surface-hover text-theme-text-tertiary'
            }`}
          >
            {enabledCount}/{totalCount}
          </span>
        )}
      </button>
      <div
        className={`transition-all duration-200 ease-in-out ${
          isExpanded
            ? 'max-h-[2000px] opacity-100 overflow-y-auto pr-2'
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="space-y-1 bg-theme-surface/30 border-x border-b border-theme-border/50 rounded-b-lg p-3">
          {showToggleAll && totalCount > 1 && onToggleAll && (
            <div className="flex justify-end gap-2 mb-2 pb-2 border-b border-theme-border/50">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAll(sectionKey, true);
                }}
                disabled={allEnabled}
                className={`text-xs px-2 py-1 rounded transition ${
                  allEnabled
                    ? 'text-theme-text-tertiary cursor-not-allowed'
                    : 'text-theme-primary hover:text-theme-primary-hover hover:bg-theme-primary/10'
                }`}
              >
                Enable all
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAll(sectionKey, false);
                }}
                disabled={atMinimum}
                className={`text-xs px-2 py-1 rounded transition ${
                  atMinimum
                    ? 'text-theme-text-tertiary cursor-not-allowed'
                    : 'text-theme-text-secondary hover:text-theme-text hover:bg-theme-surface-hover'
                }`}
              >
                Disable all
              </button>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Section Group (collapsible header wrapping related sections)
// =============================================================================

export function SectionGroup({
  title,
  subtitle,
  children,
  defaultExpanded = true,
}: SectionGroupProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-2 px-1 group"
      >
        <div className="flex items-center gap-2">
          <ChevronRight
            className={`w-4 h-4 text-theme-primary transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
          />
          <div className="text-left">
            <h3 className="text-sm font-bold text-theme-primary uppercase tracking-wider">
              {title}
            </h3>
            <p className="text-[10px] text-theme-text-tertiary">{subtitle}</p>
          </div>
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isExpanded ? 'max-h-2500 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pl-1">{children}</div>
      </div>
    </div>
  );
}
